type Point = { x: number; y: number };

const cross = (a: Point, b: Point, c: Point) =>
  (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
const epsilon = 1e-6;
const onSegment = (a: Point, b: Point, p: Point) =>
  Math.abs(cross(a, b, p)) < epsilon &&
  p.x >= Math.min(a.x, b.x) - epsilon &&
  p.x <= Math.max(a.x, b.x) + epsilon &&
  p.y >= Math.min(a.y, b.y) - epsilon &&
  p.y <= Math.max(a.y, b.y) + epsilon;
const intersects = (a: Point, b: Point, c: Point, d: Point) =>
  (cross(a, b, c) * cross(a, b, d) < 0 &&
    cross(c, d, a) * cross(c, d, b) < 0) ||
  onSegment(a, b, c) ||
  onSegment(a, b, d) ||
  onSegment(c, d, a) ||
  onSegment(c, d, b);

/** Points must be in screen CSS pixels, so the closing target survives zooming. */
export const roofOutlineAction = (
  corners: Point[],
  cursor: Point
): 'append' | 'close' | 'ignore' => {
  if (!corners.length) return 'append';
  const start = corners[0];
  const last = corners[corners.length - 1];
  const closing = Math.hypot(cursor.x - start.x, cursor.y - start.y) <= 12;
  if (closing && corners.length < 3) return 'ignore';
  const end = closing ? start : cursor;
  if (
    corners.some(
      (p, i) =>
        !(closing && i === 0) && Math.hypot(p.x - end.x, p.y - end.y) < 1
    )
  )
    return 'ignore';
  // Reject retracing the most recent edge, including collinear backtracking.
  if (
    corners.length >= 2 &&
    (onSegment(corners[corners.length - 2], last, end) ||
      onSegment(last, end, corners[corners.length - 2]))
  ) {
    return 'ignore';
  }
  for (let i = closing ? 1 : 0; i < corners.length - 2; i++) {
    if (intersects(corners[i], corners[i + 1], last, end)) return 'ignore';
  }
  if (!closing) return 'append';
  // The closing edge may touch the first edge only at the starting corner.
  if (
    onSegment(start, corners[1], last) ||
    onSegment(last, start, corners[1])
  ) {
    return 'ignore';
  }
  const area = corners.reduce((sum, p, i) => {
    const next = corners[(i + 1) % corners.length];
    return sum + p.x * next.y - next.x * p.y;
  }, 0);
  return Math.abs(area) > epsilon ? 'close' : 'ignore';
};
